/**
 * Entwickler-Telemetrie (Masterprompt Abschnitt 32).
 *
 * Zeigt Bildrate, Latenz und Serverzustand an. Diese Anzeige ist ausschliesslich
 * fuer die Entwicklung gedacht und erscheint deshalb nur, solange die Seite mit
 * "npm run dev" laeuft. In einer fertigen, gebauten Version ist sie nicht
 * vorhanden - der Code wird beim Bauen komplett entfernt.
 */

import { useState } from 'react';
import { getCreep } from '@dueline/game-data';
import { DevActionType, type DevActionTypeValue } from '@dueline/shared';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';

/** True, solange der Entwicklungsserver laeuft. */
export const IS_DEV_BUILD = import.meta.env.DEV;

/**
 * Die Creeps im Dev-Menue - in BEIDE Richtungen dieselben (Robin, 12.10.2026).
 *
 * Vorher waren die beiden Gruppen verschieden gross, und man musste beim
 * Vergleichen hin- und herdenken. Jetzt steht links wie rechts dasselbe, nur
 * die Richtung ist eine andere: losschicken oder auf sich zulaufen lassen.
 *
 * Die Aufschrift kommt aus den SPIELDATEN, nicht aus der Textdatei. So kann
 * auf einem Knopf nie etwas anderes stehen, als er schickt.
 */
const DEV_CREEPS = [
  { id: 'minion', send: DevActionType.SendMinion, spawn: DevActionType.SpawnMinion },
  { id: 'brute', send: DevActionType.SendBrute, spawn: DevActionType.SpawnBrute },
  { id: 'runner', send: DevActionType.SendRunner, spawn: DevActionType.SpawnRunner },
  { id: 'raider', send: DevActionType.SendRaider, spawn: DevActionType.SpawnRaider },
] as const;

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="dl-stat">
      <span className="dl-stat__label">{label}</span>
      <span className="dl-stat__value">{value}</span>
    </div>
  );
}

/** Eine Reihe Creep-Knoepfe - einmal zum Losschicken, einmal als Gegner. */
function CreepButtons({
  richtung,
  onDevAction,
}: {
  richtung: 'send' | 'spawn';
  onDevAction: (action: DevActionTypeValue) => void;
}) {
  return (
    <div className="dl-dev-menu__buttons">
      {DEV_CREEPS.map((creep) => (
        <button
          key={`${richtung}-${creep.id}`}
          type="button"
          onClick={() => onDevAction(creep[richtung])}
        >
          {getCreep(creep.id)?.name ?? creep.id}
        </button>
      ))}
    </div>
  );
}

export function DebugPanel({
  snapshot,
  onDevAction,
}: {
  snapshot: MatchSnapshot;
  onDevAction: (action: DevActionTypeValue) => void;
}) {
  const [open, setOpen] = useState(true);

  if (!IS_DEV_BUILD) return null;

  if (!open) {
    return (
      <button type="button" className="dl-panel" onClick={() => setOpen(true)}>
        {t.debug.show}
      </button>
    );
  }

  /*
   * Meilenstein und Tastenbelegung standen bis zum 12.10.2026 ueber den
   * Messwerten. Robin braucht nur die Werte und das Dev-Menue - alles davor
   * ist raus, damit das Feld nicht halb aus Text besteht.
   */
  return (
    <div className="dl-panel">
      <div className="dl-panel__title">{t.debug.title}</div>
      <Row label={t.debug.fps} value={snapshot.fps} />
      <Row label={t.debug.frameTime} value={`${snapshot.frameTimeMs} ms`} />
      <Row label={t.debug.ping} value={`${snapshot.pingMs} ms`} />
      <Row label={t.debug.serverTick} value={snapshot.tick} />
      <Row label={t.debug.tickRate} value={snapshot.tickRate} />
      <Row label={t.debug.creeps} value={snapshot.creepCount} />

      {/* Dev-Menue (Abschnitt 96): nur, wenn AUCH der Server Dev-Aktionen
          annimmt. In Produktion fehlt es doppelt - der Client baut es nicht
          ein, und der Server wuerde die Aktionen ohnehin verwerfen. */}
      {snapshot.devMode && (
        <div className="dl-dev-menu">
          <div className="dl-panel__title">{t.dev.title}</div>
          <div className="dl-dev-menu__buttons">
            <button type="button" onClick={() => onDevAction(DevActionType.AddGold100)}>
              {t.dev.addGold100}
            </button>
            <button type="button" onClick={() => onDevAction(DevActionType.AddGold1000)}>
              {t.dev.addGold1000}
            </button>
            <button type="button" onClick={() => onDevAction(DevActionType.AddIncome50)}>
              {t.dev.addIncome50}
            </button>
            <button type="button" onClick={() => onDevAction(DevActionType.AddXp250)}>
              {t.dev.addXp250}
            </button>
          </div>

          <div className="dl-panel__title">{t.dev.sendTitle}</div>
          <CreepButtons richtung="send" onDevAction={onDevAction} />

          <div className="dl-panel__title">{t.dev.spawnTitle}</div>
          <CreepButtons richtung="spawn" onDevAction={onDevAction} />
        </div>
      )}
      <button type="button" className="dl-panel" onClick={() => setOpen(false)}>
        {t.debug.hide}
      </button>
    </div>
  );
}
